import { Navigate, Route, Routes, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Login from './pages/Login.jsx'
import Redirect from './pages/Redirect.jsx'
import { apiFetch } from './utils/api.js'
import './App.css'

const Empresas = lazy(() => import('./pages/Empresas.jsx'))
const Webhooks = lazy(() => import('./pages/Webhooks.jsx'))
const Pedidos = lazy(() => import('./pages/Pedidos.jsx'))
const Compras = lazy(() => import('./pages/Compras.jsx'))
const Aprovados = lazy(() => import('./pages/Aprovados.jsx'))
const Aduana = lazy(() => import('./pages/Aduana.jsx'))
const Faturar = lazy(() => import('./pages/Faturar.jsx'))
const Despachar = lazy(() => import('./pages/Despachar.jsx'))
const EmTransito = lazy(() => import('./pages/EmTransito.jsx'))
const Finalizados = lazy(() => import('./pages/Finalizados.jsx'))
const Anuncios = lazy(() => import('./pages/Anuncios.jsx'))
const Revisao = lazy(() => import('./pages/Revisao.jsx'))
const Atendimento = lazy(() => import('./pages/Atendimento.jsx'))
const Chat = lazy(() => import('./pages/Chat.jsx'))
const Perguntas = lazy(() => import('./pages/Perguntas.jsx'))
const Sac = lazy(() => import('./pages/Sac.jsx'))
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'))
const Financeiro = lazy(() => import('./pages/Financeiro.jsx'))
const Despesas = lazy(() => import('./pages/Despesas.jsx'))
const Inter = lazy(() => import('./pages/Inter.jsx'))
const Erros = lazy(() => import('./pages/Erros.jsx'))

/* Prefetch das páginas mais prováveis quando o browser estiver idle */
const prefetchRoutes = () => {
  import('./pages/Pedidos.jsx')
  import('./pages/Anuncios.jsx')
  import('./pages/Revisao.jsx')
  import('./pages/Chat.jsx')
  import('./pages/Sac.jsx')
  import('./pages/Dashboard.jsx')
}
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(prefetchRoutes)
  } else {
    setTimeout(prefetchRoutes, 2000)
  }
}

function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const resolveMobileTitle = (path) => {
    const normalized = String(path || '/').split('?')[0]
    if (normalized.startsWith('/sac')) return 'SAC'
    if (normalized.startsWith('/chat')) return 'Chat'
    if (normalized.startsWith('/empresas')) return 'Empresas'
    if (normalized.startsWith('/dashboard')) return 'Dashboard'
    if (normalized.startsWith('/pedidos/aprovados')) return 'Aprovados'
    if (normalized.startsWith('/pedidos/compras')) return 'Compras'
    if (normalized.startsWith('/pedidos/aduana')) return 'Aduana'
    if (normalized.startsWith('/pedidos/faturar')) return 'Faturar'
    if (normalized.startsWith('/pedidos/despachar')) return 'Despachar'
    if (normalized.startsWith('/pedidos/em-transito')) return 'Em transito'
    if (normalized.startsWith('/pedidos/finalizados')) return 'Finalizados'
    if (normalized.startsWith('/pedidos/todos')) return 'Todos os pedidos'
    if (normalized.startsWith('/pedidos')) return 'Pedidos'
    if (normalized.startsWith('/compras')) return 'Compras'
    if (normalized.startsWith('/anuncios')) return 'Anuncios'
    if (normalized.startsWith('/financeiro')) return 'Financeiro'
    if (normalized.startsWith('/despesas')) return 'Despesas'
    if (normalized.startsWith('/inter')) return 'Integrações'
    if (normalized.startsWith('/webhooks')) return 'Webhooks'
    if (normalized.startsWith('/revisao')) return 'Revisão'
    if (normalized.startsWith('/atendimento')) return 'Atendimento'
    if (normalized.startsWith('/perguntas')) return 'Perguntas'
    if (normalized.startsWith('/erros')) return 'Erros'
    return 'Onda X'
  }
  const [mobileTitle, setMobileTitle] = useState(() => resolveMobileTitle(location.pathname))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const pendingStorageKey = 'ondax_nav_pending_seen'
  const [navPending, setNavPending] = useState({
    pedidos: 0,
    sac: 0,
    chat: 0,
    perguntas: 0,
    atendimento: 0,
    webhooks: 0,
  })
  const [navTotals, setNavTotals] = useState({
    pedidos: 0,
    sac: 0,
    chat: 0,
    perguntas: 0,
    atendimento: 0,
    webhooks: 0,
  })
  const [pedidosStageCounts, setPedidosStageCounts] = useState({
    aprovados: 0,
    compras: 0,
    aduana: 0,
    faturar: 0,
    despachar: 0,
    em_transito: 0,
    finalizados: 0,
    todos: 0,
  })
  const readPendingSeen = () => {
    if (typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(pendingStorageKey)
      return raw ? JSON.parse(raw) : {}
    } catch (error) {
      console.warn('[NAV] Falha ao ler pendências lidas:', error)
      return {}
    }
  }

  const [, setPendingSeen] = useState(() => readPendingSeen())

  const persistPendingSeen = (next) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(pendingStorageKey, JSON.stringify(next))
    } catch (error) {
      console.warn('[NAV] Falha ao salvar pendências lidas:', error)
    }
  }

  const applyPendingVisibility = (totals, seen) => ({
    pedidos: Math.max(0, Number(totals.pedidos || 0) - Number(seen.pedidos || 0)),
    sac: Math.max(0, Number(totals.sac || 0) - Number(seen.sac || 0)),
    chat: Math.max(0, Number(totals.chat || 0) - Number(seen.chat || 0)),
    perguntas: Math.max(0, Number(totals.perguntas || 0) - Number(seen.perguntas || 0)),
    atendimento: Math.max(0, Number(totals.atendimento || 0) - Number(seen.atendimento || 0)),
    webhooks: Math.max(0, Number(totals.webhooks || 0) - Number(seen.webhooks || 0)),
  })

  const markNavStart = (path) => {
    if (typeof window === 'undefined' || !window?.performance) return
    window.__ondaxNavStart = performance.now()
    window.__ondaxNavTarget = path
    console.log(`[PERF][NAV] click path=${path}`)
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !window?.performance) return
    const now = performance.now()
    const navStart = typeof window.__ondaxNavStart === 'number' ? window.__ondaxNavStart : now
    const routeChangeMs = now - navStart
    console.log(`[PERF][NAV] route_change path=${location.pathname} ms=${routeChangeMs.toFixed(1)}`)
    requestAnimationFrame(() => {
      const firstRenderMs = performance.now() - navStart
      console.log(`[PERF][NAV] first_render path=${location.pathname} ms=${firstRenderMs.toFixed(1)}`)
      window.__ondaxNavStart = null
      window.__ondaxNavTarget = null
    })
  }, [location.pathname])

  useEffect(() => {
    let active = true
    let inFlight = false

    const loadPending = async () => {
      if (typeof document !== 'undefined' && document.hidden) {
        return
      }
      if (inFlight) {
        console.warn('[NAV] loadPending ignorado (request anterior ainda em andamento)')
        return
      }
      inFlight = true
      try {
        const [response, supportResponse] = await Promise.all([
          apiFetch('/api/dashboard'),
          apiFetch('/api/support/summary'),
        ])
        if (!response.ok) throw new Error('dashboard_failed')
        const payload = await response.json()
        const supportPayload = supportResponse.ok ? await supportResponse.json() : null
        const ordersPending = Number(payload?.orders?.byStatus?.new || 0)
        const sacPending = Number(payload?.sac?.overdue || 0) + Number(payload?.sac?.dueToday || 0) + Number(payload?.sac?.withoutResponse || 0)
        const chatPending = Number(payload?.chat?.withoutResponse || 0)
        const questionsPending = Number(payload?.questions?.withoutResponse || 0)
        const supportPending = Number(supportPayload?.summary?.acao_necessaria ?? (sacPending + chatPending + questionsPending))
        const webhooksPending = Number(payload?.webhooks?.withError || 0)
        if (active) {
          const totals = {
            pedidos: ordersPending,
            sac: sacPending,
            chat: chatPending,
            perguntas: questionsPending,
            atendimento: supportPending,
            webhooks: webhooksPending,
          }
          const latestSeen = readPendingSeen()
          setPendingSeen(latestSeen)
          setNavTotals(totals)
          setNavPending(applyPendingVisibility(totals, latestSeen))
        }
      } catch (error) {
        console.warn('[NAV] Falha ao carregar pendências:', error?.message || error)
      } finally {
        inFlight = false
      }
    }

    loadPending()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const baseTitle = resolveMobileTitle(location.pathname)
    const updateTitle = () => {
      const override = typeof window !== 'undefined' ? window.__ondaxMobileTitle : ''
      setMobileTitle(override || baseTitle)
    }
    updateTitle()
    if (typeof window === 'undefined') return undefined
    window.addEventListener('ondax-mobile-title', updateTitle)
    return () => {
      window.removeEventListener('ondax-mobile-title', updateTitle)
    }
  }, [location.pathname])

  useEffect(() => {
    setMobileNavOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    let active = true
    const loadUser = async () => {
      try {
        const response = await apiFetch('/api/auth/me')
        if (!response.ok) return
        const payload = await response.json().catch(() => null)
        if (active) setCurrentUser(payload?.user || null)
      } catch {
        // menu continua funcional mesmo sem dados do usuário
      }
    }
    loadUser()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const mobileQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 768px)') : null
    if (!mobileQuery?.matches) return

    const prev = document.body.style.overflow
    document.body.style.overflow = mobileNavOpen ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileNavOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    if (typeof document === 'undefined') return
    const active = document.activeElement
    if (active && typeof active.blur === 'function') {
      requestAnimationFrame(() => {
        try {
          active.blur()
        } catch {
          // ignore
        }
      })
    }
  }, [mobileNavOpen])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const root = document.documentElement
    const vv = window.visualViewport

    const update = () => {
      const height = vv?.height || window.innerHeight || 0
      if (!height) return
      root.style.setProperty('--app-dvh', `${Math.round(height)}px`)
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    if (vv && typeof vv.addEventListener === 'function') {
      vv.addEventListener('resize', update)
      vv.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
      if (vv && typeof vv.removeEventListener === 'function') {
        vv.removeEventListener('resize', update)
        vv.removeEventListener('scroll', update)
      }
    }
  }, [])

  const isPedidosSection = String(location.pathname || '').startsWith('/pedidos')
  const isSupportSection = String(location.pathname || '').startsWith('/atendimento')

  useEffect(() => {
    if (!isPedidosSection) return

    let active = true
    let inFlight = false

    const loadPedidosStageCounts = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const [aprovadosRes, comprasRes, aduanaRes, faturarRes, despacharRes, emTransitoRes, finalizadosRes, todosRes] = await Promise.all([
          apiFetch('/api/orders?status=APPROVED&flowStage=approved&page=1&limit=1'),
          apiFetch('/api/compras?stage=compras&page=1&limit=1'),
          apiFetch('/api/compras?stage=aduana&page=1&limit=1'),
          apiFetch('/api/compras?stage=faturar&page=1&limit=1'),
          apiFetch('/api/compras?stage=despachar&page=1&limit=1'),
          apiFetch('/api/compras?stage=em_transito&page=1&limit=1'),
          apiFetch('/api/compras?stage=finalizados&page=1&limit=1'),
          apiFetch('/api/orders?page=1&limit=1'),
        ])

        const readTotal = async (response) => {
          if (!response?.ok) return 0
          const payload = await response.json().catch(() => null)
          const total = Number(payload?.total || 0)
          return Number.isFinite(total) ? total : 0
        }

        const next = {
          aprovados: await readTotal(aprovadosRes),
          compras: await readTotal(comprasRes),
          aduana: await readTotal(aduanaRes),
          faturar: await readTotal(faturarRes),
          despachar: await readTotal(despacharRes),
          em_transito: await readTotal(emTransitoRes),
          finalizados: await readTotal(finalizadosRes),
          todos: await readTotal(todosRes),
        }

        if (active) setPedidosStageCounts(next)
      } catch {
        if (active) {
          setPedidosStageCounts({
            aprovados: 0,
            compras: 0,
            aduana: 0,
            faturar: 0,
            despachar: 0,
            em_transito: 0,
            finalizados: 0,
            todos: 0,
          })
        }
      } finally {
        inFlight = false
      }
    }

    loadPedidosStageCounts()
    return () => {
      active = false
    }
  }, [isPedidosSection])

  const handleNavClick = (to, pendingKey) => {
    markNavStart(to)
    setMobileNavOpen(false)
    setUserMenuOpen(false)
    if (!pendingKey) return
    setPendingSeen((prev) => {
      const next = { ...prev, [pendingKey]: Number(navTotals[pendingKey] || 0) }
      persistPendingSeen(next)
      setNavPending(applyPendingVisibility(navTotals, next))
      return next
    })
  }

  const handleLogout = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' })
    } catch {
      // mesmo se o servidor falhar, limpa o estado local e volta ao login
    }
    try { window.localStorage.removeItem('ondax:token') } catch {}
    try { window.sessionStorage.removeItem('ondax:returnTo') } catch {}
    navigate('/login', { replace: true })
  }

  const userName = currentUser?.name || currentUser?.fullName || currentUser?.username || currentUser?.email || 'ondax'
  const userInitials = String(userName)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'OX'

  const renderNavLink = (to, label, pendingKey) => {
    const pendingCount = pendingKey ? Number(navPending[pendingKey] || 0) : 0
    const iconByLabel = {
      Empresas: '▦',
      Dashboard: '▥',
      Pedidos: '▣',
      Anuncios: '◇',
      Financeiro: '$',
      Despesas: '▤',
      'Integrações': '⌁',
      Webhooks: '⌘',
      'Revisão': '✓',
      Atendimento: '☏',
      Erros: '!',
    }
    const icon = iconByLabel[label] || '◦'
    return (
      <NavLink
        to={to}
        onClick={() => handleNavClick(to, pendingKey)}
        className={({ isActive }) => `app-link ${isActive ? 'active' : ''} ${pendingCount ? 'pending' : ''}`}
      >
        <span className="app-link-icon" aria-hidden="true">{icon}</span>
        <span className="app-link-text">{label}</span>
        {pendingCount > 0 ? <span className="app-link-badge">{pendingCount}</span> : null}
      </NavLink>
    )
  }

  const renderSubNavLink = (to, label, count) => (
    <NavLink
      to={to}
      onClick={() => handleNavClick(to)}
      className={({ isActive }) => `app-subnav-link ${isActive ? 'active' : ''}`}
    >
      {label} ({Number(count || 0)})
    </NavLink>
  )

  const renderNavGroup = (title, icon, children) => (
    <section className="app-nav-group" aria-label={title}>
      <div className="app-nav-group-title">
        <span aria-hidden="true">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="app-nav-group-items">{children}</div>
    </section>
  )

  return (
    <div className={`app app-top-mode app-menu-visible ${isSupportSection ? 'app-support-mode' : ''} ${isPedidosSection ? 'app-pedidos-mode' : ''}`}>
      <input
        type="checkbox"
        id="app-mobile-nav-toggle"
        className="app-mobile-toggle"
        checked={mobileNavOpen}
        onChange={(event) => setMobileNavOpen(event.target.checked)}
      />
      <header className="app-header">
        <div className="app-mobile-bar">
          <button type="button" className="app-mobile-back" onClick={() => navigate(-1)} aria-label="Voltar">
            ←
          </button>
          <div className="app-mobile-title">{mobileTitle}</div>
          <button
            type="button"
            className="app-mobile-menu"
            aria-label={mobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setMobileNavOpen((prev) => !prev)}
          >
            ☰
          </button>
        </div>
        <div className="app-header-line">
          <div className="app-top-actions">
            <button type="button" className="app-top-icon" aria-label="Notificações">⌁</button>
            <button type="button" className="app-top-icon" aria-label="Ajuda">?</button>
            <div className={`app-user-menu-wrap ${userMenuOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="app-user-chip"
                aria-haspopup="true"
                aria-expanded={userMenuOpen ? 'true' : 'false'}
                onClick={() => setUserMenuOpen((prev) => !prev)}
              >
                <span className="app-user-avatar" aria-hidden="true">{userInitials}</span>
                <span className="app-user-meta">
                  <strong>{userName}</strong>
                  <span>Operações</span>
                </span>
                <span aria-hidden="true">⌄</span>
              </button>
              <div className="app-user-dropdown" role="menu">
                <button type="button" disabled>Minha conta</button>
                <button type="button" onClick={handleLogout}>Sair do sistema</button>
              </div>
            </div>
          </div>
        </div>
        <nav className="app-nav" aria-label="Navegação principal">
          {renderNavGroup('Gestão', '▦', (
            <>
              {renderNavLink('/empresas', 'Empresas')}
              {renderNavLink('/dashboard', 'Dashboard')}
            </>
          ))}
          {renderNavGroup('Operação', '▣', (
            <>
              {renderNavLink('/pedidos/aprovados', 'Pedidos', 'pedidos')}
              {renderNavLink('/anuncios', 'Anuncios')}
              {renderNavLink('/revisao', 'Revisão')}
            </>
          ))}
          {renderNavGroup('Atendimento', '☏', (
            <>
              {renderNavLink('/atendimento', 'Atendimento', 'atendimento')}
            </>
          ))}
          {renderNavGroup('Financeiro', '$', (
            <>
              {renderNavLink('/financeiro', 'Financeiro')}
              {renderNavLink('/despesas', 'Despesas')}
            </>
          ))}
          {renderNavGroup('Técnico', '⌘', (
            <>
              {renderNavLink('/inter', 'Integrações')}
              {renderNavLink('/webhooks', 'Webhooks', 'webhooks')}
              {renderNavLink('/erros', 'Erros')}
            </>
          ))}
        </nav>
        <button
          type="button"
          className="app-mobile-overlay"
          aria-label="Fechar menu"
          onClick={() => setMobileNavOpen(false)}
        />
      </header>
      {isPedidosSection ? (
        <div className="app-subnav" aria-label="Subnavegação de Pedidos">
          {renderSubNavLink('/pedidos/aprovados', 'Aprovados', pedidosStageCounts.aprovados)}
          {renderSubNavLink('/pedidos/compras', 'Compras', pedidosStageCounts.compras)}
          {renderSubNavLink('/pedidos/aduana', 'Aduana', pedidosStageCounts.aduana)}
          {renderSubNavLink('/pedidos/faturar', 'Faturar', pedidosStageCounts.faturar)}
          {renderSubNavLink('/pedidos/despachar', 'Despachar', pedidosStageCounts.despachar)}
          {renderSubNavLink('/pedidos/em-transito', 'Em transito', pedidosStageCounts.em_transito)}
          {renderSubNavLink('/pedidos/finalizados', 'Finalizados', pedidosStageCounts.finalizados)}
          {renderSubNavLink('/pedidos/todos', 'Todos', pedidosStageCounts.todos)}
        </div>
      ) : null}
      <main className="app-content">
        <Outlet />
      </main>
    </div>
  )
}

function AuthGate() {
  const navigate = useNavigate()
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const checkedOnceRef = useRef(false)
  const authInitTestUsedRef = useRef(false)
  const AUTH_INIT_TIMEOUT_MS = 10000
  const AUTH_INIT_RETRY_DELAY_MS = 1200
  const AUTH_INIT_MAX_ATTEMPTS = 3

  useEffect(() => {
    if (checkedOnceRef.current) return
    checkedOnceRef.current = true
    let active = true
    const currentPath = location.pathname
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    const authInitTestMode = (() => {
      if (typeof window === 'undefined') return ''
      const params = new URLSearchParams(window.location.search || '')
      return String(params.get('authInitTest') || '').toLowerCase()
    })()
    const isTransientAuthInitError = (error) => {
      const msg = String(error?.message || error || '').toLowerCase()
      return (
        msg.includes('aborted')
        || msg.includes('abort')
        || msg.includes('timeout')
        || msg.includes('failed to fetch')
        || msg.includes('network')
      )
    }

    const fetchAuthMe = async (timeoutMs = AUTH_INIT_TIMEOUT_MS) => {
      if (!authInitTestUsedRef.current && (authInitTestMode === 'timeout' || authInitTestMode === '401')) {
        authInitTestUsedRef.current = true
        if (authInitTestMode === '401') {
          return new Response(null, { status: 401 })
        }
        throw new Error('simulated_timeout')
      }
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
      try {
        return await apiFetch('/api/auth/me', { signal: controller.signal })
      } finally {
        clearTimeout(timeoutId)
      }
    }
    const tryRefresh = async () => {
      const refresh = await apiFetch('/api/auth/refresh')
      if (!refresh.ok) return false
      const retry = await apiFetch('/api/auth/me')
      if (!retry.ok) return false
      await retry.json()
      console.log('[AUTH_INIT_SUCCESS]', { path: currentPath, mode: 'refresh' })
      if (active) {
        console.log('[AUTH_INIT_DONE]', { checking: false, source: 'refresh' })
        setChecking(false)
      }
      return true
    }
    const check = async () => {
      try {
        console.log('[AUTH_INIT_START]', { path: currentPath })
        for (let attempt = 1; attempt <= AUTH_INIT_MAX_ATTEMPTS; attempt += 1) {
          try {
            const timeoutForAttempt = Math.min(AUTH_INIT_TIMEOUT_MS + ((attempt - 1) * 2000), 16000)
            const res = await fetchAuthMe(timeoutForAttempt)

            if (res.ok) {
              await res.json()
              console.log('[AUTH_INIT_SUCCESS]', { path: currentPath, status: res.status, attempt })
              if (active) {
                console.log('[AUTH_INIT_DONE]', { checking: false, source: attempt > 1 ? 'retry' : 'success' })
                setChecking(false)
              }
              return
            }

            if (res.status === 401) {
              const refreshed = await tryRefresh()
              if (refreshed) return
              throw new Error('unauthorized')
            }

            if (attempt < AUTH_INIT_MAX_ATTEMPTS) {
              console.warn('[AUTH_INIT_RETRY]', { path: currentPath, status: res.status, attempt })
              await wait(AUTH_INIT_RETRY_DELAY_MS * attempt)
              continue
            }

            throw new Error(`auth_me_status_${res.status}`)
          } catch (err) {
            const transient = isTransientAuthInitError(err) || String(err?.message || '').includes('simulated_timeout')
            if (transient && attempt < AUTH_INIT_MAX_ATTEMPTS) {
              console.warn('[AUTH_INIT_TRANSIENT]', { path: currentPath, error: err?.message || String(err), attempt })
              await wait(AUTH_INIT_RETRY_DELAY_MS * attempt)
              continue
            }
            throw err
          }
        }
      } catch (err) {
        console.error('[AUTH_INIT_FAIL]', { path: currentPath, error: err?.message || String(err) })
        if (active) {
          navigate('/login', { replace: true, state: { from: currentPath } })
        }
      }
    }
    check()
    return () => {
      active = false
    }
  }, [navigate, location.pathname])

  if (checking) {
    return (
      <div className="page">
        <div className="empty-state">Carregando...</div>
      </div>
    )
  }

  return <Layout />
}

function PageLoader() {
  return (
    <div className="page">
      <div className="empty-state">Carregando...</div>
    </div>
  )
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<AuthGate />}>
          <Route index element={<Navigate to="empresas" replace />} />
          <Route path="empresas" element={<Empresas />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="pedidos" element={<Navigate to="/pedidos/aprovados" replace />} />
          <Route path="pedidos/aprovados" element={<Aprovados />} />
          <Route path="pedidos/compras" element={<Compras />} />
          <Route path="pedidos/aduana" element={<Aduana />} />
          <Route path="pedidos/faturar" element={<Faturar />} />
          <Route path="pedidos/despachar" element={<Despachar />} />
          <Route path="pedidos/em-transito" element={<EmTransito />} />
          <Route path="pedidos/finalizados" element={<Finalizados />} />
          <Route path="pedidos/todos" element={<Pedidos />} />
          <Route path="compras" element={<Navigate to="/pedidos/compras" replace />} />
          <Route path="anuncios" element={<Anuncios />} />
          <Route path="revisao" element={<Revisao />} />
          <Route path="financeiro" element={<Financeiro />} />
          <Route path="despesas" element={<Despesas />} />
          <Route path="inter" element={<Inter />} />
          <Route path="atendimento" element={<Atendimento />} />
          <Route path="perguntas" element={<Perguntas />} />
          <Route path="erros" element={<Erros />} />
          <Route path="chat" element={<Chat />} />
          <Route path="sac" element={<Sac />} />
          <Route path="*" element={<Navigate to="empresas" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
